/**
 * EntityProfile — renders a full Archive Profile (Entity Research Standard).
 *
 * Intelligence reference file, not a biography. Sections:
 * opening strip → portrait + identity panel → profile body (markdown with
 * inline evidence-category badges) → Evidence Register → timeline →
 * known associates → Continue Investigating → sources → principle footer.
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { EntityImageFallback } from "./EntityImageFallback";
import {
  Fingerprint, Scale, Clock, Users, Compass, Link2, BookOpen, ArrowUpRight,
} from "lucide-react";

const GROUP_PATH = { person: "/people", organization: "/organizations", operation: "/operations" };

const CATEGORY_TONE = (cat) => {
  const c = (cat || "").toLowerCase();
  if (c === "convicted") return { fg: "#FF6B5E", bd: "#D92D20", bg: "#D92D2016" };
  if (["established fact", "confirmed by court", "government finding",
    "parliamentary or congressional finding", "inspector general finding",
    "official record", "historical consensus"].includes(c))
    return { fg: "#5FC96C", bd: "#2E7D3A", bg: "#3FB95012" };
  if (["credible allegation", "reported association", "investigated", "charged"].includes(c))
    return { fg: "#D9B44A", bd: "#8A6D1F", bg: "#C9A22712" };
  if (["acquitted", "exonerated", "officially denied"].includes(c))
    return { fg: "#7FB3E0", bd: "#3D6C94", bg: "#6EA8D812" };
  if (["disputed claim", "ongoing historical debate"].includes(c))
    return { fg: "#E08B4A", bd: "#9C5A22", bg: "#D97B2D12" };
  return { fg: "#9A948A", bd: "#4A453D", bg: "#8A837712" }; // unsupported / other
};

const CategoryBadge = ({ label, small }) => {
  const t = CATEGORY_TONE(label);
  return (
    <span
      className={`inline-flex items-center font-mono uppercase align-middle ${small ? "text-[8.5px] tracking-[0.18em] px-1.5 py-[2px]" : "text-[9.5px] tracking-[0.22em] px-2 py-[3px]"}`}
      style={{ color: t.fg, border: `1px solid ${t.bd}`, background: t.bg, borderRadius: 2 }}
      data-testid="evidence-category-badge"
    >
      {label}
    </span>
  );
};

/* Inline entity links — contextual, first-mention-per-section only.
   Entries come from profile.inline_link_map (server-resolved canonical
   entities; ambiguous labels already excluded). */
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildLinkCtx = (linkMap) => {
  if (!linkMap || !linkMap.length) return null;
  const byLabel = new Map(linkMap.map((m) => [m.label, m]));
  const pattern = linkMap.map((m) => escapeRe(m.label)).join("|");
  return {
    byLabel,
    regex: new RegExp(`(?<![\\w-])(${pattern})(?![\\w-])`, "g"),
    used: new Set(),
  };
};

const linkify = (text, key, ctx) => {
  if (!ctx || !text) return [text];
  const out = [];
  let last = 0, k = 0, m;
  ctx.regex.lastIndex = 0;
  while ((m = ctx.regex.exec(text)) !== null) {
    const entry = ctx.byLabel.get(m[1]);
    if (!entry || ctx.used.has(entry.slug)) continue;
    ctx.used.add(entry.slug);
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <Link
        key={`${key}-l${k++}`}
        to={`${GROUP_PATH[entry.kind_group] || "/people"}/${entry.slug}`}
        data-testid={`inline-entity-link-${entry.slug}`}
        className="text-[#E4DFD3] hover:text-[#D92D20] transition-colors"
        style={{ borderBottom: "1px dotted #A68F5E66", textDecoration: "none" }}
      >
        {m[1]}
      </Link>
    );
    last = m.index + m[1].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

/* Inline renderer: **[Category]** → badge · **bold** · *italic* */
const inline = (text, key, linkCtx) => {
  if (!text) return null;
  const out = [];
  let i = 0, k = 0, buf = "";
  const flush = () => {
    if (buf) {
      out.push(...(linkCtx ? linkify(buf, `${key}-t${k++}`, linkCtx) : [buf]));
      buf = "";
    }
  };
  while (i < text.length) {
    if (text.startsWith("**[", i)) {
      const end = text.indexOf("]**", i + 3);
      if (end > i) {
        flush();
        out.push(<CategoryBadge key={`${key}-c${k++}`} label={text.slice(i + 3, end)} small />);
        out.push(" ");
        i = end + 3;
        continue;
      }
    }
    if (text.startsWith("**", i)) {
      const end = text.indexOf("**", i + 2);
      if (end > i + 1) {
        flush();
        out.push(<strong key={`${key}-b${k++}`} className="text-[#E4DFD3] font-semibold">{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (text[i] === "*" && text[i + 1] !== "*" && (i === 0 || text[i - 1] !== "*")) {
      const end = text.indexOf("*", i + 1);
      if (end > i + 1 && text[end + 1] !== "*") {
        flush();
        out.push(<em key={`${key}-i${k++}`} className="italic text-[#E4DFD3]/95">{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    buf += text[i++];
  }
  flush();
  return out;
};

const tokenise = (md) => {
  const blocks = [];
  let para = [], list = null;
  const flushP = () => { if (para.length) { blocks.push({ t: "p", text: para.join(" ") }); para = []; } };
  const flushL = () => { if (list) { blocks.push({ t: "ul", items: list }); list = null; } };
  for (const raw of (md || "").replace(/\r\n/g, "\n").split("\n")) {
    const line = raw.trimEnd();
    let m = line.match(/^##\s+(.+?)\s*$/);
    if (m) { flushP(); flushL(); blocks.push({ t: "h2", text: m[1] }); continue; }
    m = line.match(/^###\s+(.+?)\s*$/);
    if (m) { flushP(); flushL(); blocks.push({ t: "h3", text: m[1] }); continue; }
    m = line.match(/^[-•*]\s+(.+)$/);
    if (m) { flushP(); list = list || []; list.push(m[1]); continue; }
    if (!line.trim()) { flushP(); flushL(); continue; }
    flushL(); para.push(line);
  }
  flushP(); flushL();
  return blocks;
};

const SectionLabel = ({ icon: Icon, children, count }) => (
  <div className="flex items-center gap-3 mb-5">
    <Icon size={13} className="text-[#D92D20]" />
    <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#D92D20]">{children}</span>
    {count != null && (
      <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#454038]">
        {String(count).padStart(2, "0")}
      </span>
    )}
  </div>
);

export const EntityProfile = ({ profile }) => {
  const blocks = useMemo(() => tokenise(profile?.profile_md), [profile]);
  if (!profile) return null;
  const p = profile.portrait;

  return (
    <div data-testid="entity-profile" className="border-t" style={{ borderColor: "#2D2A26" }}>
      {/* ── Opening strip ─────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pt-12 md:pt-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.45em] text-[#7B7168] mb-4">
          Archive Profile // Ref: {profile.slug}
        </div>
        <p className="text-[17px] md:text-[19px] leading-relaxed text-[#E4DFD3] max-w-3xl" data-testid="profile-summary">
          {profile.summary}
        </p>
      </section>

      {/* ── Portrait + Identity ───────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16 grid grid-cols-1 md:grid-cols-[340px_1fr] gap-10 md:gap-14">
        <div>
          {p ? (
            <figure data-testid="profile-portrait" className="border"
              style={{ borderColor: "#2D2A26", background: "#141317" }}>
              <div className="relative p-2 pb-0 flex items-center justify-center"
                style={{ background: "#0B0A0D" }}>
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`}
                  alt={profile.name}
                  className="max-w-full h-auto object-contain max-h-[260px] md:max-h-[360px]"
                  style={{ filter: "sepia(0.14) contrast(1.02)" }}
                />
                <div className="absolute top-2 left-2 w-4 h-4 border-t border-l" style={{ borderColor: "#A68F5E" }} />
                <div className="absolute bottom-0 right-2 w-4 h-4 border-b border-r" style={{ borderColor: "#A68F5E" }} />
              </div>
              <figcaption className="p-4">
                <div className="font-heading text-[17px] text-[#E4DFD3] leading-tight">{profile.name}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mt-1 mb-3">
                  {(profile.kind || "").replace(/_/g, " ")}
                </div>
                <dl className="space-y-2 border-t pt-3" style={{ borderColor: "#221F1C" }}
                  data-testid="portrait-attribution">
                  <div>
                    <dt className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#5A554C]">Source</dt>
                    <dd className="text-[12px] text-[#B9B2A4]">{p.source_name || "Wikimedia Commons"}</dd>
                  </div>
                  {p.image_type && (
                    <div>
                      <dt className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#5A554C]">Type</dt>
                      <dd className="text-[12px] text-[#B9B2A4]" data-testid="portrait-image-type">{p.image_type}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#5A554C]">License</dt>
                    <dd className="text-[12px] text-[#B9B2A4]">{p.license || "—"}</dd>
                  </div>
                  {p.creator && (
                    <div>
                      <dt className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#5A554C]">Photographer</dt>
                      <dd className="text-[12px] text-[#B9B2A4]">{p.creator}</dd>
                    </div>
                  )}
                  {p.year && (
                    <div>
                      <dt className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#5A554C]">Year</dt>
                      <dd className="text-[12px] text-[#B9B2A4]">{p.year}</dd>
                    </div>
                  )}
                </dl>
                <details className="mt-3 group" data-testid="photo-information-toggle">
                  <summary className="cursor-pointer list-none font-mono text-[9px] uppercase tracking-[0.25em] text-[#A68F5E] hover:text-[#D92D20] transition-colors select-none">
                    Photo Information <span className="inline-block group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <div className="mt-2 space-y-2">
                    {p.caption && (
                      <p className="text-[11.5px] leading-relaxed text-[#8A8377]">{p.caption}</p>
                    )}
                    {p.collection && (
                      <p className="text-[11px] leading-snug text-[#6B665C]">{p.collection}</p>
                    )}
                    <a href={p.source_url} target="_blank" rel="noreferrer"
                      className="inline-block font-mono text-[9px] uppercase tracking-[0.2em] text-[#A68F5E] hover:text-[#D92D20] transition-colors"
                      data-testid="portrait-original-link">
                      View original ↗
                    </a>
                  </div>
                </details>
              </figcaption>
            </figure>
          ) : (
            <div className="border p-6 text-center space-y-3"
              style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
              data-testid="documented-identity-placeholder">
              <EntityImageFallback kind={profile.kind}
                                   className="w-full h-[180px] md:h-[220px]"
                                   iconSize={26} />
              <div className="pt-2 border-t text-left mx-auto max-w-[220px]" style={{ borderColor: "#221F1C" }}>
                <div className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#5A554C] mb-2">Known through</div>
                {["Government records", "Court records", "Historical reporting", "Primary sources"].map((s) => (
                  <div key={s} className="text-[11.5px] text-[#8A8377] leading-relaxed flex gap-2">
                    <span className="text-[#454038]">·</span>{s}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <SectionLabel icon={Fingerprint}>Identity</SectionLabel>
          <dl className="divide-y" style={{ borderColor: "#221F1C" }} data-testid="identity-facts">
            {(profile.identity_facts || []).map((f, i) => (
              <div key={i} className="grid grid-cols-[130px_1fr] md:grid-cols-[170px_1fr] gap-4 py-2.5"
                style={{ borderColor: "#221F1C" }}>
                <dt className="font-mono text-[9.5px] uppercase tracking-[0.25em] text-[#8A8377] pt-0.5">{f.label}</dt>
                <dd className="text-[13.5px] text-[#E4DFD3]/90 leading-snug">{f.value}</dd>
              </div>
            ))}
          </dl>
          {profile.why_in_archive && (
            <div className="mt-8 border-l-2 pl-4 md:pl-5" style={{ borderColor: "#A68F5E" }}>
              <div className="font-mono text-[9.5px] uppercase tracking-[0.35em] text-[#A68F5E] mb-2">
                Why this matters <span className="text-[#5A554C]">// Archive relevance</span>
              </div>
              <p className="text-[14px] leading-relaxed text-[#CFC9BC]" data-testid="why-in-archive">
                {profile.why_in_archive}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── Profile body — inline entity links, first mention per section ── */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 pb-6" data-testid="profile-body">
        {(() => {
          const linkCtx = buildLinkCtx(profile.inline_link_map);
          return blocks.map((b, i) => {
            if (b.t === "h2") {
              if (linkCtx) linkCtx.used.clear(); // reset per section
              return (
                <h2 key={i} className="font-mono text-[11px] uppercase tracking-[0.5em] text-[#D92D20] mt-14 mb-5 flex items-center gap-3">
                  <span className="h-px w-8 shrink-0" style={{ background: "#D92D20" }} />{b.text}
                </h2>
              );
            }
            if (b.t === "h3") return (
              <h3 key={i} className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#A68F5E] mt-8 mb-2">{b.text}</h3>
            );
            if (b.t === "ul") return (
              <ul key={i} className="space-y-2 my-4">
                {b.items.map((it, j) => (
                  <li key={j} className="flex gap-3 text-[15px] leading-relaxed text-[#E4DFD3]/90">
                    <span className="text-[#A68F5E] shrink-0 mt-[2px]">·</span>
                    <span>{inline(it, `l${i}-${j}`, linkCtx)}</span>
                  </li>
                ))}
              </ul>
            );
            return (
              <p key={i} className="text-[15px] leading-[1.75] text-[#E4DFD3]/90 mb-4">
                {inline(b.text, `p${i}`, linkCtx)}
              </p>
            );
          });
        })()}
      </section>

      {/* ── Evidence Register ─────────────────────────────────────── */}
      {(profile.claims || []).length > 0 && (
        <section className="max-w-6xl mx-auto px-4 md:px-8 py-14" data-testid="evidence-register">
          <SectionLabel icon={Scale} count={profile.claims.length}>Evidence Register</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {profile.claims.map((c, i) => (
              <div key={i} className="border p-4 md:p-5" style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
                data-testid={`claim-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <CategoryBadge label={c.category} />
                  <span className="font-mono text-[9px] text-[#454038] tracking-[0.2em] shrink-0">
                    {(c.citation_indices || []).map((n) => `S${n + 1}`).join(" ")}
                  </span>
                </div>
                <div className="text-[14px] leading-snug text-[#E4DFD3] mb-2">{c.statement}</div>
                <div className="text-[12.5px] leading-relaxed text-[#8A8377]">{c.context}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Timeline ──────────────────────────────────────────────── */}
      {(profile.timeline || []).length > 0 && (
        <section className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="profile-timeline">
          <SectionLabel icon={Clock} count={profile.timeline.length}>Record Timeline</SectionLabel>
          <ol className="border-l ml-1" style={{ borderColor: "#2D2A26" }}>
            {profile.timeline.map((t, i) => (
              <li key={i} className="relative pl-6 md:pl-8 pb-6 last:pb-0">
                <span className="absolute -left-[3.5px] top-[7px] w-[7px] h-[7px] rotate-45"
                  style={{ background: i === 0 || i === profile.timeline.length - 1 ? "#D92D20" : "#3A3630" }} />
                <span className="font-mono text-[11px] text-[#A68F5E] tracking-[0.2em] mr-3">{t.year}</span>
                <span className="text-[14px] text-[#E4DFD3]/90 leading-relaxed">{t.event}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* ── Known Associates ──────────────────────────────────────── */}
      {(profile.known_associates || []).length > 0 && (
        <section className="max-w-6xl mx-auto px-4 md:px-8 py-10" data-testid="known-associates">
          <SectionLabel icon={Users} count={profile.known_associates.length}>Documented Associates</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
            {profile.known_associates.map((a, i) => (
              <div key={i} className="flex gap-3 text-[13.5px] leading-snug border-b pb-3"
                style={{ borderColor: "#221F1C" }}>
                <span className="text-[#E4DFD3] shrink-0 font-semibold">{a.name}</span>
                <span className="text-[#8A8377]">{a.relation}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 font-mono text-[9.5px] uppercase tracking-[0.25em] text-[#5A554C]">
            Documented association ≠ participation in misconduct
          </div>
        </section>
      )}

      {/* ── Continue Investigating ────────────────────────────────── */}
      {((profile.related_dossiers || []).length > 0 || (profile.related_entities || []).length > 0) && (
        <section className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="continue-investigating">
          <SectionLabel icon={Compass}>Continue Investigating</SectionLabel>
          {(profile.related_dossiers || []).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
              {profile.related_dossiers.map((r) => (
                <Link key={r.slug} to={`/topic/${r.slug}`}
                  data-testid={`profile-related-dossier-${r.slug}`}
                  className="group border p-4 hover:border-[#D92D20] transition-colors"
                  style={{ borderColor: "#2D2A26", background: "#0F0E12" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-[14px] font-heading text-[#E4DFD3] group-hover:text-[#D92D20] transition-colors leading-tight">
                      {r.title}
                    </div>
                    <ArrowUpRight size={13} className="text-[#454038] group-hover:text-[#D92D20] shrink-0 transition-colors" />
                  </div>
                  {r.note && <div className="mt-1.5 text-[12px] text-[#8A8377] leading-snug">{r.note}</div>}
                </Link>
              ))}
            </div>
          )}
          {(profile.related_entities || []).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {profile.related_entities.map((r) => (
                <Link key={r.slug} to={`${GROUP_PATH[r.kind_group] || "/organizations"}/${r.slug}`}
                  data-testid={`profile-related-entity-${r.slug}`}
                  className="group border p-4 hover:border-[#D92D20] transition-colors"
                  style={{ borderColor: "#2D2A26", background: "#0F0E12" }}>
                  <div className="flex items-start justify-between gap-3">
                    <span className="inline-flex items-center gap-2 font-mono text-[12px] text-[#E4DFD3] group-hover:text-[#D92D20] transition-colors leading-tight">
                      <Link2 size={10} className="text-[#7B7168] shrink-0" />
                      {r.name}
                    </span>
                    <span
                      className="font-mono text-[8px] uppercase tracking-[0.18em] px-1.5 py-[2px] shrink-0"
                      style={r.strength === "CORROBORATED"
                        ? { color: "#5FC96C", border: "1px solid #2E7D3A", borderRadius: 2 }
                        : { color: "#D9B44A", border: "1px solid #8A6D1F", borderRadius: 2 }}
                      title={`Co-documented in ${r.co_count || 1} shared dossier${(r.co_count || 1) === 1 ? "" : "s"}`}
                      data-testid={`connection-strength-${r.slug}`}
                    >
                      {r.strength || "REPORTED"}
                    </span>
                  </div>
                  {r.note && <div className="mt-1.5 text-[12px] text-[#8A8377] leading-snug">{r.note}</div>}
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Sources ───────────────────────────────────────────────── */}
      {(profile.sources || []).length > 0 && (
        <section className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="profile-sources">
          <SectionLabel icon={BookOpen} count={profile.sources.length}>Sources · All HTTP-Verified</SectionLabel>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2.5">
            {profile.sources.map((s, i) => (
              <li key={i} className="flex gap-3 text-[13px] leading-snug">
                <span className="font-mono text-[10px] text-[#A68F5E] tracking-wide shrink-0 pt-0.5">S{i + 1}</span>
                <div className="min-w-0">
                  <a href={s.url} target="_blank" rel="noreferrer"
                    className="text-[#E4DFD3]/90 hover:text-[#D92D20] transition-colors">
                    {s.title}
                  </a>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.15em] text-[#5A554C] ml-2">
                    {s.publisher} · {s.stance}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* ── Principle footer ──────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-14">
        <div className="border-t pt-6 font-mono text-[10px] uppercase tracking-[0.25em] leading-relaxed text-[#5A554C]"
          style={{ borderColor: "#2D2A26" }} data-testid="archive-principle">
          The Archive does not decide guilt. The Archive documents evidence. Inclusion in a profile never implies guilt —
          every claim above carries its evidence status and traces to a listed source.
        </div>
      </section>
    </div>
  );
};

export default EntityProfile;
